import { GA4_EVENT_NAMES } from 'constants/analytics';

type AnalyticsPayload = Record<string, unknown>;

interface EcommerceItem {
  item_id?: string;
  item_name?: string;
  item_category?: string;
  price?: number;
  quantity?: number;
  item_variant?: string;
  coupon?: string;
}

interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

const AICPA_PREFIX = 'aicpa_';
const stripAicpaPrefix = (data: AnalyticsPayload): AnalyticsPayload => {
  return Object.keys(data).reduce((acc, key) => {
    const cleanKey = key.startsWith(AICPA_PREFIX) ? key.slice(AICPA_PREFIX.length) : key;
    acc[cleanKey] = data[key];
    return acc;
  }, {} as AnalyticsPayload);
};

const buildEcommercePayload = (data: AnalyticsPayload): { items: EcommerceItem[]; value?: number; currency?: string } | null => {
  const names = data[`${AICPA_PREFIX}product_name`] as string[] | undefined
    || data.product_name as string[] | undefined;
  const ids = data[`${AICPA_PREFIX}product_id`] as string[] | undefined
    || data.product_id as string[] | undefined;
  const prices = data[`${AICPA_PREFIX}product_price`] as number[] | undefined
    || data.product_price as number[] | undefined;
  const categories = data[`${AICPA_PREFIX}product_category`] as string[] | undefined
    || data.product_category as string[] | undefined;
  const quantities = data[`${AICPA_PREFIX}product_quantity`] as number[] | undefined
    || data.product_quantity as number[] | undefined;
  const skus = data[`${AICPA_PREFIX}product_sku`] as string[] | undefined
    || data.product_sku as string[] | undefined;

  if (!ids?.length && !names?.length) return null;

  const length = Math.max(ids?.length || 0, names?.length || 0);
  const items: EcommerceItem[] = Array.from({ length }, (_, i) => ({
    item_id: ids?.[i] || '',
    item_name: names?.[i] || '',
    item_category: categories?.[i] || '',
    price: prices?.[i] || 0,
    quantity: quantities?.[i] ?? 1,
    item_variant: skus?.[i] || '',
  }));

  const cartAmount =
    (data[`${AICPA_PREFIX}cart_amount`] as number | undefined) ??
    (data.cart_amount as number | undefined) ??
    items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  return {
    items,
    value: cartAmount,
    currency: data[`${AICPA_PREFIX}currency`] as string || data.currency as string || 'USD',
  };
};

const buildGTMEvent = (eventName: string, data: AnalyticsPayload): DataLayerEvent => {
  const gtmEventName = GA4_EVENT_NAMES[eventName] || eventName;
  const cleanData = stripAicpaPrefix(data);

  const { analyticsEvent, ...rest } = cleanData;

  const ecommerceEvents = [
    'add_to_cart', 'remove_from_cart', 'view_cart', 'begin_checkout',
    'add_payment_info', 'purchase', 'view_item',
  ];

  const event: DataLayerEvent = {
    event: gtmEventName,
    ...rest,
  };

  if (ecommerceEvents.includes(gtmEventName)) {
    const ecommerce = buildEcommercePayload(data);
    if (ecommerce) {
      event.ecommerce = ecommerce;
    }
  }

  if (gtmEventName === 'purchase') {
    event.transaction_id = rest.order_number || rest.transaction_id || '';
    event.affiliation = 'AICPA & CIMA';
    if (event.ecommerce) {
      (event.ecommerce as any).transaction_id = event.transaction_id;
    }
  }

  return event;
};

const pushToDataLayer = (event: DataLayerEvent): void => {
  if (typeof window === 'undefined') return;

  (window as any).dataLayer = (window as any).dataLayer || [];

  if (event.ecommerce) {
    (window as any).dataLayer.push({ ecommerce: null });
  }

  (window as any).dataLayer.push(event);

  if (process.env.NODE_ENV !== 'production') {
    console.groupCollapsed(`[Analytics] GTM event: ${event.event}`);
    console.log(event);
    console.groupEnd();
  }
};

const view = (data: AnalyticsPayload): void => {
  const eventName = (data.analyticsEvent as string) || 'page_view';
  pushToDataLayer(buildGTMEvent(eventName, data));
};

const link = (data: AnalyticsPayload): void => {
  const eventName = (data.analyticsEvent as string) || 'event';
  pushToDataLayer(buildGTMEvent(eventName, data));
};

const push = (eventName: string, data: Record<string, unknown> = {}): void => {
  pushToDataLayer({ event: eventName, ...data });
};

const appInitialized = (
  memberType: string,
  userId: string,
  aicpaUid: string,
  roles: string[] = []
): void => {
  push('app_initialized', {
    appInitialized: true,
    member_type: memberType || 'authenticated-no-membership',
    user_id: userId || '',
    aicpa_uid: aicpaUid || '',
    roles: roles,
    is_auth: true,
  });
};

const pageView = (pathname: string, additionalData: Record<string, unknown> = {}): void => {
  push('page_view', {
    page_path: pathname,
    page_title: typeof document === 'undefined' ? '' : document.title,
    ...additionalData,
  });
};

export const analyticsLayer = {
  view,
  link,
  push,
  appInitialized,
  pageView,
};

export default analyticsLayer;
